<template>
  <PlatformRenderer>
    <template v-slot:onMobile>
      <div class="w-100">
        <WCSpinner v-if="loading" position="center" size="xl" />
        <WCBuyItAgainSection
          :items="items"
          class="mx-4"
          v-else-if="!loadFailError && items.length > 0"
        />

        <div v-else-if="loadFailError">{{ loadFailError }}</div>
        <div class="text-center mt-3" v-else-if="!items.length">
          {{ $t('buyItAgainItemsUnavailable') }}
        </div>
        <div
          class="d-flex flex-column align-items-center py-4"
          :class="{ 'sr-only': loading || !items.length }"
        >
          <WCPagination
            class="m-0"
            :collectionSize="totalCount"
            v-model="pn"
            :pageSize="Number(ps)"
            :maxSize="7"
            @input="pnChanged"
            :page-links="false"
          />
          <div class="text-center mt-3" aria-live="assertive">
            {{ $t('showing') }} {{ startRecord }} {{ $t('to') }} {{ endRecord }} {{ $t('of') }}
            {{ totalCount }} {{ $t('results') }}
            <span class="sr-only">{{ $t('forText', { name: $t('buyItAgain') }) }}</span> ({{
              pagesAvailable
            }}
            {{ $t('pages') }})
          </div>
        </div>
      </div>
    </template>
    <template v-slot:onDesktop>
      <DashboardWindow>
        <!-- START: Title Section -->
        <template v-slot:title>{{ $t('buyItAgain') }}</template>
        <!-- END: Title Section -->
        <template v-slot:body>
          <WCSpinner v-if="loading" position="center" size="xl" />
          <DashboardWindowSection v-else class="font-size-lg mb-0">
            <WCBuyItAgainSection :items="items" v-if="!loadFailError && items.length > 0" />
            <div v-else-if="loadFailError">{{ loadFailError }}</div>
            <div class="text-center" v-else-if="!items.length">
              {{ $t('buyItAgainItemsUnavailable') }}
            </div>
          </DashboardWindowSection>
          <div
            class="d-flex justify-content-between align-items-center p-3 bg-white"
            :class="{ 'sr-only': loading || !items.length }"
          >
            <WCPagination
              class="m-0"
              :collectionSize="totalCount"
              v-model="pn"
              :pageSize="Number(ps)"
              :maxSize="7"
              @input="pnChanged"
            />
            <span class="text-right ml-3" aria-live="assertive">
              {{ $t('showing') }} {{ startRecord }} {{ $t('to') }} {{ endRecord }} {{ $t('of') }}
              {{ totalCount }} {{ $t('results') }}
              <span class="sr-only">{{ $t('forText', { name: $t('buyItAgain') }) }}</span> ({{
                pagesAvailable
              }}
              {{ $t('pages') }})
            </span>
          </div>
        </template>
      </DashboardWindow>
    </template>
  </PlatformRenderer>
</template>

<script>
import axios from 'axios';
import PlatformRenderer from '@/modules/platform/components/PlatformRenderer';
import DashboardWindow from '@/layouts/components/Dashboard/DashboardWindow.vue';
import DashboardWindowSection from '@/layouts/components/Dashboard/DashboardWindowSection.vue';
import WCSpinner from '@/components/WCSpinner/WCSpinner.vue';
import WCBuyItAgainSection from '@/modules/user/components/WCBuyItAgainSection/WCBuyItAgainSection.vue';
import WCPagination from '@/components/WCPagination/WCPagination.vue';

export default {
  name: 'BuyItAgain',
  components: {
    WCSpinner,
    PlatformRenderer,
    DashboardWindowSection,
    DashboardWindow,
    WCBuyItAgainSection,
    WCPagination,
  },
  data() {
    return {
      loading: true,
      items: [],
      loadFailError: null,
      ps: 30,
      pn: 1,
      totalCount: 0,
    };
  },
  /**
   * Created lifecycle method to fetch all buy it again products
   */
  async created() {
    await this.getBuyItAgainItemsList();
  },
  computed: {
    /**
     * Computed properties for pagination
     */
    startRecord() {
      return this.totalCount > 0 ? (this.pn - 1) * this.ps + 1 : 0;
    },
    endRecord() {
      return Math.min(this.totalCount, this.pn * this.ps);
    },
    pagesAvailable() {
      return Math.ceil(this.totalCount / this.ps);
    },
  },
  watch: {
    $route: 'getBuyItAgainItemsList', // Anytime $router is used, the getBuyItAgainItemsList method is called
  },
  methods: {
    /**
     * Method to get the 'buy it again' products list
     */
    async getBuyItAgainItemsList() {
      const { pn = 1, ps = 30 } = this.$route.query;
      this.loading = true;
      try {
        const response = await axios.post('api/me/items', {
          pn,
          ps,
        });
        this.totalCount = response.data.totalCount;
        if (response.data.code === 0) {
          this.items = response.data.data;
        }
        this.loadFailError = null;

        this.pn = Number(pn);
        this.ps = Number(ps);
      } catch (e) {
        this.loadFailError = e.response?.data?.message;
      } finally {
        this.loading = false;
      }
    },

    pnChanged(pageNumber) {
      this.$router.push({
        name: 'Buy It Again',
        query: { ...this.$route.query, pn: pageNumber === 1 ? undefined : pageNumber },
      });
    },
  },
};
</script>
<style lang="scss" scoped>
.wc-account-window-section-desktop {
  margin-bottom: 0 !important;
}
</style>
